import {
  getFieldTypeOcticon,
  type IssueFieldSingleSelectOption,
  toIssueFieldType,
} from '@github-ui/issue-fields-shared/issue-field'
import {
  getIssueFieldSelectionPresentationValue,
  type IssueFieldWithUnsavedValue,
} from '@github-ui/issue-metadata/IssueFieldWithUnsavedValue'
import {TooltipTriggerWrapper} from '@github-ui/issue-metadata/TooltipTriggerWrapper'
import {SharedPicker} from '@github-ui/item-picker-base/SharedPicker'
import {colorNames, useNamedColor} from '@github-ui/use-named-color'
import {GlobeIcon, type IconProps} from '@primer/octicons-react'
import {type ButtonProps, Tooltip, useId} from '@primer/react'

import {getVisibilityIndicatorTooltip} from '../constants/labels'
import styles from './MetadataFooter.module.css'

// Matches the label picker pattern, which shows the first two color dots in the anchor.
const MAX_DISPLAYED_MULTI_SELECT_DOTS = 2

const GlobeIconWithProps = (props: IconProps) => <GlobeIcon {...props} size={12} />

export type IssueFieldValueSharedEditorAnchorProps = {
  fieldWithValue: IssueFieldWithUnsavedValue
  buttonProps?: ButtonProps
  className?: string
  readonly?: boolean
  showVisibilityIndicator?: boolean
  isOwnerEnterpriseManaged?: boolean
}

export const IssueFieldValueSharedEditorAnchor = ({
  fieldWithValue,
  buttonProps,
  className,
  readonly,
  showVisibilityIndicator,
  isOwnerEnterpriseManaged,
}: IssueFieldValueSharedEditorAnchorProps) => {
  const fieldType = toIssueFieldType(fieldWithValue.field.dataType)
  const text = getIssueFieldSelectionPresentationValue(fieldType, fieldWithValue.value)
  const leadingIconElement = getLeadingIconElement(fieldWithValue)
  const anchorText = text ? `${fieldWithValue.field.name}:` : fieldWithValue.field.name!
  const visibilityTooltipId = useId()

  const picker = (
    <SharedPicker
      key={fieldWithValue.field.id}
      anchorText={anchorText}
      sharedPickerMainValue={text}
      leadingIcon={getFieldTypeOcticon(fieldType)}
      leadingIconElement={leadingIconElement}
      trailingIcon={showVisibilityIndicator ? GlobeIconWithProps : undefined}
      anchorProps={buttonProps}
      className={className}
      readonly={readonly}
      visibilityTooltipId={showVisibilityIndicator ? visibilityTooltipId : undefined}
    />
  )
  return showVisibilityIndicator ? (
    <Tooltip
      id={visibilityTooltipId}
      text={getVisibilityIndicatorTooltip(isOwnerEnterpriseManaged)}
      type="description"
      direction="s"
      delay="medium"
    >
      <TooltipTriggerWrapper>{picker}</TooltipTriggerWrapper>
    </Tooltip>
  ) : (
    picker
  )
}

const getLeadingIconElement = (fieldWithValue: IssueFieldWithUnsavedValue) => {
  if (fieldWithValue.field.dataType === 'SINGLE_SELECT' && fieldWithValue.value) {
    const option = fieldWithValue.value as IssueFieldSingleSelectOption
    const color = option?.color || 'GRAY'

    return <SingleSelectFieldDot color={color} />
  }

  if (fieldWithValue.field.dataType === 'MULTI_SELECT' && fieldWithValue.value) {
    const options = fieldWithValue.value as IssueFieldSingleSelectOption[]
    if (options.length === 0) return null

    return (
      <div className={styles.MultiSelectFieldDots}>
        {options.slice(0, MAX_DISPLAYED_MULTI_SELECT_DOTS).map((option, index) => (
          <SingleSelectFieldDot key={option.id ?? `${option.name}-${index}`} color={option.color || 'GRAY'} />
        ))}
      </div>
    )
  }

  return null
}

const SingleSelectFieldDot = ({color}: {color: string}) => {
  const effectiveColor = colorNames.find(c => c === color)
  const {fg} = useNamedColor(effectiveColor)
  return <div style={{'--bg': `${fg}`}} className={styles.SingleSelectFieldDot} />
}
